import { useState, useEffect } from 'react';
import { useVisaValdaStore } from '../../store/VisaValdaStore';
import { usePropsDataStore } from '../../store/PropsDataStore';
import { updateSectionsProcentage } from '../../../../api/services/property';
import { formatNumber } from '../../utils';

export const useMetricData = () => {
    const { visaValda, setVisaValda } = useVisaValdaStore();

    const { props, setOneProp } = usePropsDataStore();

    const [loading, setLoading] = useState(true);

    const [totalArea, setTotalArea] = useState(0);
    const [totalVolume, setTotalVolume] = useState(0);
    const [avgGrowth, setAvgGrowth] = useState(0);
    const [totalGrowth, setTotalGrowth] = useState(0);
    const [avgStockPerHa, setAvgStockPerHa] = useState(0);
    const [TGLdata, setTGLdata] = useState({ tall: 0, gran: 0, lov: 0, total: 0 });
    const [totalActiveProps,setTotalActiveProps ] = useState(0);

    const [valuationChartData, setValuationChartData] = useState(null);
    const [fellingClassData, setFellingClassData] = useState([]);
    const [volumeAge, setVolumeAge] = useState([]);
    const [areaAge, setAreaAge] = useState([]);

    const [totalSolarHa, setTotalSolarHa] = useState(0);
    const [totalSolarEnergy, setTotalSolarEnergy] = useState(null);

    //const [totalCO2, setTotalCO2] = useState(0);
    const [totalEco, setTotalEco] = useState(null);
    const [totalArbonics, setTotalArbonics] = useState(null);

    // CO2
    const [totalCo2, setTotalCo2] = useState(0);
    const [totalAnnualCo2, setTotalAnnualCo2] = useState(0);

    // Yearly growth
    const [yearlyGrowth, setYearlyGrowth] = useState(0);

    // Sections details
    const [sectionsDetails, setSectionsDetails] = useState([]);

    const [totalPropValue, setTotalPropValue] = useState({totalValue: 0, change_perc: 0.0, activeProps: 0});

    const [ageGraphView, setAgeGraphView] = useState('area');
    const [totalOverviewClassification,setTotalOverviewClassification] = useState({ field: 0, forest_land: 0, other: 0, water: 0 });

    const [joyride, setJoyride] = useState(false);
    const [arbonicsDialogVisible, setArbonicsDialogVisible] = useState(window.location.href.includes("#co2-interest"));

    // Modals
    const [errorModal, setErrorModal] = useState(false);
    const [propDfr,setPropDfr] = useState(false);
    
    const waitForData = async () => {
        const activeProps = props.filter(p => p.active && p.status === 'data')
        const propsToFetch = activeProps.filter(p => p.data && p.ready)
        setTotalActiveProps(props.filter(p => p.active).length) 

        if(propsToFetch.length != activeProps.length) {
            setTimeout(() => waitForData(), 100)
        } else {
            setPriceLineData()
            setTotalValues()
            setLoading(false)
        }
    }

    useEffect(() => {
        if(visaValda) {
            setPriceLineData()
            setTotalValues()
            setVisaValda(false)
        }
    }, [visaValda]);
    

    useEffect(() => {
        waitForData()
    }, [props]);
    
    useEffect(() => {
        if (!totalSolarEnergy) return;
        const handleHashChange = () => window.location.hash.slice(1).toLocaleLowerCase() == "co2" && document.getElementById('solar-energy-production')?.scrollIntoView({ behavior: "smooth", block: "center" });              

        window.addEventListener("hashchange", handleHashChange);

        handleHashChange();

        return () => window.removeEventListener("hashchange", handleHashChange);
    }, [totalSolarEnergy]);

    const updateTotalVolume = (volume) => {
        let prop = props.find(p => p.active == true)

        if (volume == totalVolume) {
            return Promise.resolve()
        } 
        
        let prevValue = totalVolume
        let editValue = volume
        setTotalVolume(0)
        return updateSectionsProcentage(prop.ref, 'volume', editValue / prevValue, editValue,prop.type).then(res => {
            if(res.ok) {
                const new_data = res.data
                setTotalVolume(editValue)
            
                try {
                    let kr_m3fub = props.find(p => p.active)?.data.prop_value.price_m3sk
                    setTotalPropValue({totalValue: editValue * kr_m3fub, change_perc:totalPropValue.change_perc, activeProps: totalPropValue.activeProps})
                } catch {
                    console.log("failed to update total prop value after volume update")
                }
                
                let calculatedVolume = 0
                new_data.sections.map(s => calculatedVolume += s.volume)
                const updatedProp = { ...prop, data: new_data, volume: calculatedVolume }
            
                setOneProp(updatedProp)
            } else {
                setTotalVolume(prevValue)
                setErrorModal(true)
                console.error('Failed to update total volume')
            }
        })
        
    }

    const updateAvgGrowth = (growth) => {
        let prop = props.find(p => p.active == true)
        if (growth == avgGrowth) {
            return Promise.resolve();
        } 
        
        let prevValue = avgGrowth
        let editValue = growth
        setAvgGrowth(false)
        return updateSectionsProcentage(prop.ref, 'growth_rate', editValue / prevValue, editValue,prop.type).then(res => {
            if(res.ok) {
                const new_data = res.data
                setAvgGrowth(editValue)
                const updatedProp = { ...prop, data: new_data }
                setOneProp(updatedProp)
            } else {
                setAvgGrowth(prevValue)
                setErrorModal(true)
                console.error('Failed to update average growth')
            }
        })
    }

    const setPriceLineData = () => {
        const activeProps = props.filter(p => p.active)
        setTotalActiveProps(activeProps.length)
        
        const propsWithData = activeProps.filter(p => p.status === 'data' && p.data && p.ready && p.data.valuation_chart_data)
        
        if (propsWithData.length === 0) {
            return;
        }
        
        let curr_year = new Date().getFullYear();
        let data = {}
        let color = ''
        let bar = []
        
        propsWithData.map(prop => {
            color = prop.data.valuation_chart_data.line[0].color
            bar = prop.data.valuation_chart_data.bar

            for (let i=0; i < prop.data.valuation_chart_data.years.length; i++) {
                let year = prop.data.valuation_chart_data.years[i];
                
                //find all values for year, should only be multiple if multiple props are selected
                prop.data.valuation_chart_data.line.forEach((itm) => {
                    if (itm.data.length > 0) {
                        if(data[year]){
                            data[year].push(itm.data[i])
                        } else{
                            data[year] = [itm.data[i], ]
                        }
                    }
                })
            }
        })

        let data_list = []
        let year_list = []
        for (let year in data) {
            let avg = Math.round(data[year].reduce((a,b) => a+b, 0) / data[year].length)
            data_list.push(avg)
            year_list.push(year)
        }

        setValuationChartData({bar: bar, line: [{ color: color, data: data_list }], years: year_list, current_value: data_list[data_list.length -1]})
    }

    const setTotalValues = () => {
        const activeProps = props.filter(p => p.active)
        
        const propsWithData = activeProps.filter(p => p.status === 'data' && p.data && p.ready)
        
        const allDfr = propsWithData.every(prop => prop.dfr);
        setPropDfr(allDfr);
        setTotalActiveProps(activeProps.length)

        let fellingClasses = []
        let allGrowthRate = []
        let areaWithBonitet = 0
        let TGL = {tall: 0, gran: 0, lov: 0}

        let volumeAge = new Array(11).fill(0);
        let areaAge = new Array(11).fill(0);
        let totalCo2 = 0;
        let totalAnnualCo2 = 0;
        let totalVolume = 0;
        let sectionsDetails = [];
        let yearlyGrowth = 0;

        propsWithData.map(prop => {
            totalCo2 += Number(prop.data.co2_data.co2) || 0;
            totalAnnualCo2 += Number(prop.data.co2_data.annual_co2_growth) || 0;
            totalVolume += Number(prop.volume) || 0;
            yearlyGrowth += Number(prop?.data?.yearly_growth) || 0;

            prop.data.sections.map(sect => {
                // TGL
                TGL.tall += Math.round(sect.tall * sect.volume / 100)
                TGL.gran += Math.round(sect.gran * sect.volume / 100)
                TGL.lov += Math.round(sect.lov * sect.volume / 100)

                sectionsDetails.push({
                    nr: sect.nr,
                    avd: sect.localID,
                    designation: prop.designation,
                    hkl: sect.felling_class,
                    bonitet: Math.round(sect.growth_rate),
                    si: `${sect.major_tree}${sect.si}`,
                    age: sect.age,
                    area: sect.area,
                    tgl: {
                    pine: sect.tall,
                    fir: sect.gran,
                    maple: sect.lov,
                    },
                    volume: sect.volume,
                })

                if(fellingClasses.find(c => c.class == sect.felling_class)) {
                    fellingClasses.find(c => c.class == sect.felling_class).area += parseInt(sect.area)
                    fellingClasses.find(c => c.class == sect.felling_class).volume += sect.volume
                } else {
                    fellingClasses.push({class: sect.felling_class, area: parseInt(sect.area), volume: sect.volume})
                }

                if(parseFloat(sect.growth_rate) > 0) {
                    allGrowthRate.push(parseFloat(sect.growth_rate));
                    areaWithBonitet += Number(sect.area || 0);
                }
                
                if(sect.age > 100) {
                    volumeAge[10] += sect.volume
                    areaAge[10] += parseInt(sect.area)
                } else {
                    volumeAge[Math.floor(sect.age / 10)] += sect.volume
                    areaAge[Math.floor(sect.age / 10)] += parseInt(sect.area)
                }
            })
        })

        TGL.total = TGL.tall + TGL.gran + TGL.lov
        TGL.tall = (TGL.tall / TGL.total) * 100
        TGL.gran = (TGL.gran / TGL.total) * 100
        TGL.lov = (TGL.lov / TGL.total) * 100

        const avgGrowthValue = allGrowthRate.length > 0 
            ? allGrowthRate.reduce((total, rate) => total + rate, 0) / allGrowthRate.length 
            : 0;
        const totalGrowthValue = allGrowthRate.length > 0 
            ? Math.round(areaWithBonitet * avgGrowthValue) 
            : 0;

        setTotalVolume(totalVolume)
        setFellingClassData(fellingClasses)
        setAvgGrowth(avgGrowthValue)
        setTotalGrowth(totalGrowthValue)
        setTGLdata(TGL)
        setVolumeAge(volumeAge)
        setAreaAge(areaAge)
        setTotalCo2(formatNumber(totalCo2))
        setTotalAnnualCo2(formatNumber(totalAnnualCo2))
        setSectionsDetails(sectionsDetails)
        setYearlyGrowth(totalVolume > 0 ? (yearlyGrowth / totalVolume) * 100 : 0);

        let total = propsWithData.reduce((result, prop) => {
            // General
            result.volume += prop.volume
            result.area += prop.data.area

            // Svefa
            result.propValue.totalValue += parseInt(prop.data.prop_value.price_m3sk) * parseInt(prop.volume)
            result.propValue.change_perc += parseFloat(prop.data.prop_value.change_perc)
            result.propValue.activeProps += 1
            

            // Solar
            result.solar_ha += parseFloat(prop.data.solar_ha)
            result.solar_energy.approx_ton_paper += prop.data.solar_energy.approx_ton_paper
            result.solar_energy.approx_villas += prop.data.solar_energy.approx_villas
            result.solar_energy.arrende_per_ha += prop.data.solar_energy.arrende_per_ha
            result.solar_energy.mil_with_truck += prop.data.solar_energy.mil_with_truck
            result.solar_energy.solar_energy += prop.data.solar_energy.solar_energy

            //result.co2.annual_co2_growth += prop.data.co2_data.annual_co2_growth
            //result.co2.carbon_permits += prop.data.co2_data.carbon_permits
            //result.co2.climate_neutral_felling += prop.data.co2_data.climate_neutral_felling
            //result.co2.co2 += prop.data.co2_data.co2

            result.eco.ha += prop.data.eco_credits.ha
            result.eco.credits += prop.data.eco_credits.credits
            result.eco.sek += prop.data.eco_credits.sek_per_credit
            result.arbonics.ha += prop.arbonics?.ha ? Number(prop.arbonics.ha) : 0
            result.arbonics.price += prop.arbonics?.price ? Number(prop.arbonics.price) : 0
            result.arbonics.eligible = result.arbonics.eligible > Number(prop.data.arbonics.eligible) ? result.arbonics.eligible : Number(prop.data.arbonics.eligible)

            const oc = prop.data.overview_classification || {};
            result.overview_classification.field += oc.field ? Number(oc.field) : 0;
            result.overview_classification.forest_land += oc.forest_land ? Number(oc.forest_land) : 0;
            result.overview_classification.other += oc.other ? Number(oc.other) : 0;
            result.overview_classification.water += oc.water ? Number(oc.water) : 0;

            return result
        }, {volume: 0, area: 0, solar_ha: 0, TGL: {tall: 0, gran: 0, lov: 0}, solar_energy: {approx_ton_paper: 0, approx_villas: 0, arrende_per_ha: 0, mil_with_truck: 0, solar_energy: 0}, eco: {ha: 0, credits: 0, sek: 0}, arbonics: {ha:0, price: 0, eligible:-1}, propValue: {totalValue: 0, change_perc: 0, activeProps: 0}, overview_classification: { field: 0, forest_land: 0, other: 0, water: 0 },})
        // , co2: {annual_co2_growth: 0, carbon_permits: 0, climate_neutral_felling: 0, co2: 0}
        setTotalArea(Math.round(total.area))
        setTotalVolume(Math.round(total.volume))
        
        
        const avgStockPerHaValue = total.area > 0 ? Math.round(total.volume / total.area) : 0;
        setAvgStockPerHa(avgStockPerHaValue)

        setTotalSolarEnergy(total.solar_energy)
        setTotalSolarHa(total.solar_ha)

        //setTotalCO2(total.co2)
        setTotalEco(formatNumber(total.eco))
        setTotalArbonics(total.arbonics)
        
        setTotalPropValue({totalValue: total.propValue.totalValue, change_perc: total.propValue.activeProps > 0 ? total.propValue.change_perc / total.propValue.activeProps : total.propValue.change_perc, activeProps: total.propValue.activeProps})
        setTotalOverviewClassification(total.overview_classification)
    }

    const gotoSbpPdf = (e) => {
        let prop = props.find(p => p.active == true)

        window.location = "/map-api/sbp-to-pdf/?ref=" + prop.ref;
    }

    return {
        totalArea, totalVolume, fellingClassData, TGLdata, avgGrowth, totalGrowth, avgStockPerHa, propDfr,
        totalSolarHa, totalSolarEnergy, totalEco, totalArbonics, totalPropValue, yearlyGrowth,
        totalOverviewClassification, valuationChartData, volumeAge, areaAge, ageGraphView, loading,
        updateTotalVolume, updateAvgGrowth, totalCo2, totalAnnualCo2, sectionsDetails, totalActiveProps, gotoSbpPdf
    };
}